import { dimensions } from '@factorialco/deprecated-design-system'
import * as d3Hierarchy from 'd3-hierarchy'
import { GetJobPositionsDocument } from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'

import { useAtsEnabled } from 'modules/ats/lib/atsEnabled'
import {
  type GraphQLEmployee,
  employeeDirectlyManagedEmployees,
  employeeManagedEmployees,
} from 'modules/org_chart/lib/employeeUtils'
import { NODE_SIZE } from 'modules/org_chart/components/Node/constants'
import { findTreeNodeById } from 'modules/org_chart/lib/tree'
import type { Coordinates } from 'modules/org_chart/models/Coordinates'
import type {
  AddPositionNodeData,
  EmployeeNodeData,
  NodeData,
  NodeDataTypes,
  PositionNodeData,
} from 'modules/org_chart/models/NodeData'
import type { PointNode } from 'modules/org_chart/models/PointNode'

const queryContext = {
  suspense: false,
}

interface ReturnType {
  tree: PointNode[]
  findNodeByEmployeeId: (id: number) => PointNode | void
  traceTreePathFromEmployee: (employee: GraphQLEmployee) => number[]
  refetch: () => void
  build: (
    heads: GraphQLEmployee[],
    employees: GraphQLEmployee[],
    uncollapsedIds: Set<number>
  ) => PointNode<NodeDataTypes>[]
  initialCoordinates: Coordinates
}

const useTree = (
  heads: GraphQLEmployee[],
  employees: GraphQLEmployee[],
  uncollapsedIds: Set<number>,
  width: number,
  canManagePositions: boolean,
  canSeeJobPostings: boolean,
  canCreateJobPostings: boolean,
  allEmployees: GraphQLEmployee[],
  employeeMap: Map<number, GraphQLEmployee>
): ReturnType => {
  const { canRequestUpsell, employee: currentEmployee } = useCurrentSession()
  const { atsEnabled } = useAtsEnabled()
  const canSeePositions = atsEnabled && canManagePositions && canSeeJobPostings
  const {
    data: jobPositionsData,
    loading: fetchingJobPositions,
    refetch,
  } = useQuery(GetJobPositionsDocument, {
    context: queryContext,
    skip: !canSeePositions,
  })
  const jobPositions = jobPositionsData?.positions?.positions ?? []

  const assertManagedEmployees = (
    employee: GraphQLEmployee,
    uncollapsedIds: Set<number>
  ): EmployeeNodeData[] =>
    employeeDirectlyManagedEmployees(employee, allEmployees).map((x) =>
      buildData(x, uncollapsedIds, currentEmployee?.id === employee.id)
    )

  const assertAddPosition = (
    managerId: number,
    isManagerHigher: boolean
  ): AddPositionNodeData[] => {
    const hasPermissions =
      canSeePositions &&
      (currentEmployee?.id === managerId || isManagerHigher || canCreateJobPostings)

    if (!hasPermissions && !canRequestUpsell) {
      return []
    }

    return [
      {
        type: 'add-position',
        managerId,
        hasPermissions,
        canRequestUpsell,
      },
    ]
  }

  const assertPositions = (employeeId: number): PositionNodeData[] => {
    if (!canSeePositions) return []

    const managedPositions = jobPositions.filter((x) => x.managerId === employeeId)

    return managedPositions.map((x) => ({ type: 'position', positionId: x.id })) || []
  }

  const buildData = useUnsafeCallback(
    (
      employee: GraphQLEmployee,
      uncollapsedIds: Set<number>,
      isManagerHigher?: boolean
    ): EmployeeNodeData => {
      const id = employee.id
      const isUncollapsed = uncollapsedIds.has(id)
      const children = [
        ...assertManagedEmployees(employee, uncollapsedIds),
        ...assertPositions(employee.id),
        ...assertAddPosition(employee.id, !!isManagerHigher),
      ]

      return {
        employee,
        isUncollapsed,
        isSearchResult: false,
        hierarchyLength: employeeManagedEmployees(employee, allEmployees).length,
        children: isUncollapsed ? children : [],
        childrenLength: children.filter((x) => x.type === 'employee').length,
        type: 'employee',
      }
    },
    [uncollapsedIds, jobPositions]
  )

  const initialCoordinates = useUnsafeMemo<Coordinates>(() => {
    const [nodeWidth, nodeHeight] = NODE_SIZE

    const initialX = width / 2 - nodeWidth / 2
    const initialY = nodeHeight / 1.2
    return [initialX, initialY]
  }, [employees])

  const buildTree = useUnsafeCallback(
    (data: { children: NodeData[] }) => {
      const [nodeWidth, nodeHeight] = NODE_SIZE
      const hierarchy = d3Hierarchy.hierarchy(data)
      const treeLayout = d3Hierarchy
        .tree()
        .nodeSize([nodeWidth, nodeHeight + parseInt(dimensions.s80)])
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      const tree = treeLayout(hierarchy).descendants() as PointNode[]
      const [initialX, initialY] = initialCoordinates

      tree.forEach((x) => {
        x.x = x.x + initialX
        x.y = x.y - initialY
      })

      return tree
    },
    [employees, jobPositions]
  )

  const build = (
    heads: GraphQLEmployee[],
    employees: GraphQLEmployee[],
    uncollapsedIds: Set<number>
  ): PointNode<NodeDataTypes>[] => {
    if (!employees || fetchingJobPositions) return []

    const firstLevel = heads.reduce<EmployeeNodeData[]>((acc, x) => {
      const data = buildData(x, uncollapsedIds)

      return [...acc, data]
    }, [])

    return buildTree({ children: firstLevel })
  }

  const tree = useUnsafeMemo<PointNode[]>(
    () => build(heads, employees, uncollapsedIds),
    [heads, employees, buildData, fetchingJobPositions]
  )

  const findNodeByEmployeeId = useUnsafeCallback(
    (id: number): PointNode | void => findTreeNodeById(tree, id),
    [tree]
  )

  const traceTreePathFromEmployee = (employee: GraphQLEmployee): number[] => {
    let manager_id = employee.managerId
    let idTreePath: number[] = []

    while (manager_id) {
      const manager = employees.find((x) => x.id === manager_id)

      if (!manager) break

      idTreePath = [...idTreePath, manager.id]
      manager_id = manager.managerId
    }

    return idTreePath
  }

  return {
    tree,
    findNodeByEmployeeId,
    traceTreePathFromEmployee,
    refetch,
    initialCoordinates,
    build,
  }
}

export default useTree
